import _get from "lodash/get";
import getConfig from "next/config";
import classNames from "classnames";

import Price from "@components/Price";
import AddToCart from "@containers/Catalog/Actions/AddToCart";
import { productHelper, resizeImage } from "@lib/helpers";
import NoRatingsDataPlaceholder from "@components/Ratings/NoRatingsDataPlaceholder";
import ProductPageHeaderRatings from "./ProductPageHeaderRatings";

const {
  publicRuntimeConfig: {
    settings: { websiteName },
  },
} = getConfig();

const isAltex = websiteName === "altex";
const isMediaGalaxy = websiteName === "mediagalaxy";
const isBricoStore = websiteName === "bricostore";

// active product-tab underline: none on brico (the light-red fill is the marker
// on its own), red-brand on altex/MG
const activeTabBorder = isBricoStore ? "border-transparent" : "border-red-brand";

const ProductPageStickyHeader = ({
  isVisible,
  product,
  reviews,
  onSectionClick,
  sectionInView,
  westacoData,
  isWestacoDataValid,
  activeServices,
  hasProductAccesories,
  hasBundles,
}) => {
  const {
    isVisible: isProductVisible,
    isEol,
    isSaleable,
    hasStorePickup,
    hasResealed,
    isWestaco,
    mainOffer,
    hasOtherOffers,
  } = productHelper(product);

  // brico tabs mirror the homepage MainMenu items: hover + active use the light
  // red (red-hover), which is the active marker on its own - no underline.
  // altex/MG keep the original underline-only behavior.
  const tabClass = (section) =>
    classNames("pt-2 pb-1 border-b-[1px]", {
      "px-3": isBricoStore,
      [activeTabBorder]: sectionInView === section,
      "bg-red-hover": isBricoStore && sectionInView === section,
      "border-transparent cursor-pointer": sectionInView !== section,
      "hover:bg-red-hover": isBricoStore && sectionInView !== section,
    });

  return (
    <div
      className={`fixed w-full z-[2000] left-0 top-0 transform transition-transform ease-in-out ${
        !isVisible ? "translate-y-0" : "-translate-y-[120px]"
      }`}
    >
      <div className="hidden md:sticky md:flex md:flex-col items-center justify-between md:top-0 md:w-full h-[120px] md:z-50 bg-white">
        <div className="flex flex-row items-center justify-between space-x-4 w-full xl:max-w-7xl h-[80px] md:max-w-5xl px-4 xl:px-5">
          <div className="flex flex-row items-center space-x-3">
            <img
              src={resizeImage(product.image, "120x120")}
              className="h-[60px] w-[60px]"
              alt=""
            />

            <div className="text-base font-medium leading-5 line-clamp-2 max-w-[250px] lg:max-w-xs">
              {product.name}
            </div>
          </div>

          {!isEol && isProductVisible && (
            <>
              {!isWestaco && (
                <div className="hidden xl:flex">
                  {_get(reviews, "items[0].reviews.value", 0) ? (
                    <ProductPageHeaderRatings
                      onClick={(event) => {
                        event.preventDefault();
                        onSectionClick("reviews");
                      }}
                      reviewsCount={_get(reviews, "items[0].reviews.value", 0)}
                      ratingSummary={_get(
                        reviews,
                        "items[0].reviews.total",
                        null
                      )}
                      productUrl={`/${product.url_key}/cpd/${product.sku}/`}
                    />
                  ) : (
                    <NoRatingsDataPlaceholder
                      onClick={(event) => {
                        event.preventDefault();
                        onSectionClick("reviews");
                      }}
                      productUrl={`/${product.url_key}/cpd/${product.sku}/`}
                    />
                  )}
                </div>
              )}

              {mainOffer && (
                <Price
                  productId={parseInt(product.id)}
                  price={mainOffer.price.selling_price}
                  regularPrice={mainOffer.price.base_price}
                  msrp={mainOffer.price.msrp_price}
                  showMSRPTooltip={true}
                  formatted={true}
                  size="navbar"
                  discountType={mainOffer.price.discount_type}
                  displayDiscount={true}
                />
              )}

              <div className="flex flex-row items-center justify-end space-x-5">
                {(isSaleable || hasStorePickup) && (
                  <AddToCart
                    noMargin
                    iconOnly
                    product={product}
                    activeServices={activeServices}
                    westaco={westacoData}
                    disabled={isWestaco && !isWestacoDataValid}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div
          className={classNames("w-full", {
            "bg-[#FFC348F2]/95": isAltex,
            "bg-[#244196]/95": isMediaGalaxy,
            "bg-red-brand": isBricoStore, // same red nav bar as the homepage MainMenu
          })}
        >
          <div className="container">
            <ul
              className={classNames("flex space-x-8 select-none ", {
                "text-black": isAltex,
                "text-white": isMediaGalaxy || isBricoStore,
              })}
            >
              {hasOtherOffers && (
                <li
                  className={tabClass("oferte")}
                  onClick={() => onSectionClick("oferte")}
                >
                  Alte oferte
                </li>
              )}

              {/* TODO: Add Swogo recommendations */}

              {hasBundles && (
                <li
                  className={tabClass("pachete")}
                  onClick={() => onSectionClick("pachete")}
                >
                  Pachete Promo
                </li>
              )}

              {hasResealed > 0 && (
                <li
                  className={tabClass("resigilate")}
                  onClick={() => onSectionClick("resigilate")}
                >
                  Resigilate
                </li>
              )}

              {!isWestaco && hasProductAccesories && (
                <li
                  className={tabClass("accessories")}
                  onClick={() => onSectionClick("accessories")}
                >
                  Accesorii
                </li>
              )}

              <li
                className={tabClass("description")}
                onClick={() => onSectionClick("description")}
              >
                Descriere
              </li>

              <li
                className={tabClass("additional")}
                onClick={() => onSectionClick("additional")}
              >
                Specificatii
              </li>

              <li
                className={tabClass("reviews")}
                onClick={() => onSectionClick("reviews")}
              >
                Recenzii
              </li>

              <li
                className={tabClass("questions")}
                onClick={() => onSectionClick("questions")}
              >
                Intrebari
              </li>

              <li
                className={tabClass("compare")}
                onClick={() => onSectionClick("compare")}
              >
                Compara
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPageStickyHeader;
